import { useState } from 'react';
import { subscribe, sendCAPIEvent } from '../lib/api';
import posthog from '../lib/posthog';
import { trackMetaEvent } from '../lib/meta';
import { getAdAttribution } from '../lib/adAttribution';
import './SubscriptionModal.css';

export default function SubscriptionModal({ isOpen, onClose }) {
  const [subscribing, setSubscribing] = useState(null);

  if (!isOpen) return null;

  const handleSubscribe = async (tier) => {
    setSubscribing(tier);
    try {
      const tierPrice = tier === 'studio' ? 200 : 20;
      const attribution = getAdAttribution();
      const eventId = trackMetaEvent('InitiateCheckout', {
        value: tierPrice,
        currency: 'USD',
        content_type: 'subscription',
        num_items: 1,
      });
      sendCAPIEvent('InitiateCheckout', eventId, {
        value: tierPrice,
        currency: 'USD',
        num_items: 1,
      }, attribution).catch(() => {});
      const { fbc, fbp } = attribution;
      const successUrl = `${window.location.origin}/account?subscription=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = window.location.href.split('?')[0];
      const result = await subscribe(tier, successUrl, cancelUrl, { eventId, fbc, fbp });
      if (result.checkoutUrl) {
        posthog.capture('subscription checkout started', { tier });
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      alert('Failed to start checkout');
    } finally {
      setSubscribing(null);
    }
  };

  return (
    <div className="sub-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="sub-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sub-modal-header">
          <h2 id="sub-modal-title">Choose your plan</h2>
          <p>Start free or subscribe for unlimited images and included video allowance.</p>
        </header>

        <div className="sub-modal-plans">
          {/* Pay As You Go */}
          <article className="sub-modal-plan">
            <h3 className="sub-modal-plan__name">Pay As You Go</h3>
            <div className="sub-modal-plan__price">
              <span className="sub-modal-plan__amount">$0</span>
              <span className="sub-modal-plan__period">/mo</span>
            </div>
            <ul className="sub-modal-plan__features">
              <li>Per-model video pricing</li>
              <li>Per-model image pricing</li>
              <li>No monthly commitment</li>
              <li>Buy credits or use metered billing</li>
            </ul>
            <button
              type="button"
              className="sub-modal-plan__btn sub-modal-plan__btn--secondary"
              onClick={onClose}
            >
              Continue free
            </button>
          </article>

          {/* Creator */}
          <article className="sub-modal-plan sub-modal-plan--featured">
            <div className="sub-modal-plan__badge">Best Value</div>
            <h3 className="sub-modal-plan__name">Creator</h3>
            <div className="sub-modal-plan__price">
              <span className="sub-modal-plan__amount">$20</span>
              <span className="sub-modal-plan__period">/mo</span>
            </div>
            <ul className="sub-modal-plan__features">
              <li><strong>$30/mo video allowance</strong></li>
              <li><strong>Unlimited image generation</strong></li>
              <li><strong>Unlimited voice generation</strong></li>
              <li>Pay-as-you-go after allowance</li>
              <li>Priority processing</li>
            </ul>
            <button
              type="button"
              className="sub-modal-plan__btn sub-modal-plan__btn--primary"
              disabled={!!subscribing}
              onClick={() => handleSubscribe('creator')}
            >
              {subscribing === 'creator' ? 'Loading...' : 'Subscribe to Creator'}
            </button>
          </article>

          {/* Studio */}
          <article className="sub-modal-plan">
            <h3 className="sub-modal-plan__name">Studio</h3>
            <div className="sub-modal-plan__price">
              <span className="sub-modal-plan__amount">$200</span>
              <span className="sub-modal-plan__period">/mo</span>
            </div>
            <ul className="sub-modal-plan__features">
              <li><strong>$500/mo video allowance</strong></li>
              <li><strong>Unlimited image generation</strong></li>
              <li><strong>Unlimited voice generation</strong></li>
              <li>Pay-as-you-go after allowance</li>
              <li>Priority processing</li>
              <li>Best for high-volume creators</li>
            </ul>
            <button
              type="button"
              className="sub-modal-plan__btn sub-modal-plan__btn--primary"
              disabled={!!subscribing}
              onClick={() => handleSubscribe('studio')}
            >
              {subscribing === 'studio' ? 'Loading...' : 'Subscribe to Studio'}
            </button>
          </article>
        </div>

        <button type="button" className="sub-modal-skip" onClick={onClose}>
          Skip for now — I'll decide later
        </button>
      </div>
    </div>
  );
}
